import { IConfiguration, IOrder, MAKE, OrderSource, isSkipCrystallize } from 'next-common';
import { useEffect, useRef } from 'react';
import { validateModelSeriesName } from '../../utils';
import { isEmpty } from 'lodash';
import { useClientLazyQuery } from './useClientLazyQuery';
import { IVehicleModels } from '@common';
import { NEXT_ENTITY } from '@common/interfaces';
import { setVehicleModels } from '@store/slices/vehicleModelsSlice';
import { useAppDispatch } from '@store/hooks';
import { setIsVehicleListDataLoaded } from '@store/slices/salesWizardSlice';

export interface IVehicleModelsProps {
  make: MAKE;
  modelSeries: string;
  source: OrderSource;
  serialNo: string;
  configuration: IConfiguration;
  setIsLoading: (loadingState: boolean) => void;
  orderType: string;
  order: IOrder;
}
export interface IVehicleModelsQuery {
  vehicleModels: IVehicleModels;
  isVehicleListDataLoaded: boolean;
}

const isValidCall = (props: IVehicleModelsProps): boolean => {
  const isSkipPimCall = isSkipCrystallize(props.configuration, props.order);

  if (props.make && props.modelSeries && props.source && !isSkipPimCall) {
    return true;
  }
  return false;
};

export const useVehicleModelsQuery = (props: IVehicleModelsProps) => {
  const dispatch = useAppDispatch();
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // Only trigger fetch if the fetchKey has changed AND at least one of the core fetch props changed (not just currentStep etc)
    const fetchKey = [
      props.make,
      props.modelSeries,
      props.source,
      props.serialNo,
      isSkipCrystallize(props.configuration, props.order) ? 'skip' : 'fetch',
    ]
      .map((value) => value ?? '')
      .join('|');

    const prevKey = lastFetchKeyRef.current;
    if (isValidCall(props)) {
      if (prevKey === fetchKey) {
        return;
      }
      lastFetchKeyRef.current = fetchKey;
      props.setIsLoading(true);
      vehicleModels()
        .then((response) => {
          props?.setIsLoading(false);
          return response;
        })
        .catch((error) => {
          props?.setIsLoading(false);
          console.error(error);
        });
    } else {
      props?.setIsLoading(false);
    }
  }, [props.make, props.modelSeries, props.source, props.configuration, props.serialNo]);

  const input = {
    make: props.make,
    modelSeries:
      props.make === MAKE.Smart
        ? validateModelSeriesName(props.modelSeries?.toLowerCase())
        : props.modelSeries?.toLowerCase(),
  };
  const [vehicleModels, { loading, data, error }] = useClientLazyQuery(NEXT_ENTITY.VEHICLE_MODELS, {
    variables: {
      input,
    },
    nextOptions: { revalidate: 60 },
    onCompleted: (data) => {
      dispatch(setVehicleModels(data));
    },
  });
  return { data: data, loading, error };
};

export const useVehicleModels = (props: IVehicleModelsProps): IVehicleModelsQuery => {
  const dispatch = useAppDispatch();
  const { data } = useVehicleModelsQuery(props);
  const isVehicleListDataLoaded = isValidCall(props) ? isEmpty(data) : false;
  dispatch(setIsVehicleListDataLoaded(isVehicleListDataLoaded));
  return { vehicleModels: data, isVehicleListDataLoaded };
};
